"use client";

import Link from "next/link";
import { useState } from "react";

import type { Dictionary, Locale } from "@/lib/i18n";

import { ZayaMark } from "../layout/zaya-mark";

type ForgotPasswordScreenProps = {
  dictionary: Dictionary;
  locale: Locale;
};

export function ForgotPasswordScreen({
  dictionary,
  locale,
}: ForgotPasswordScreenProps) {
  const [identifier, setIdentifier] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const t = dictionary.forgotPassword;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!identifier.trim()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await fetch("/api/auth/password-reset-request", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ identifier: identifier.trim() }),
      });
      const payload = (await response.json()) as {
        ok?: boolean;
        error?: string;
      };
      if (!response.ok) {
        setError(payload.error ?? t.errors.generic);
        return;
      }
      setSubmitted(true);
    } catch {
      setError(t.errors.generic);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main
      className="auth-layout"
      data-locale={locale}
      dir={locale === "ar" ? "rtl" : "ltr"}
    >
      <section className="auth-card">
        <div className="auth-copy">
          <ZayaMark />
          <p className="page-eyebrow">{t.eyebrow}</p>
          <h1>{t.title}</h1>
          <p>{t.description}</p>
        </div>

        {submitted ? (
          <div className="auth-success">
            <h2>{t.successTitle}</h2>
            <p>{t.successDescription}</p>
            <Link className="button-link" href={`/${locale}/login`}>
              {t.backToLogin}
            </Link>
          </div>
        ) : (
          <form className="auth-form" onSubmit={handleSubmit}>
            <label className="form-field">
              <span>{t.identifierLabel}</span>
              <input
                autoComplete="username"
                className="form-input"
                onChange={(event) => setIdentifier(event.target.value)}
                placeholder={t.identifierPlaceholder}
                required
                type="text"
                value={identifier}
              />
            </label>

            {error ? <p className="form-error-banner">{error}</p> : null}

            <div className="form-actions">
              <button
                className="button-link"
                disabled={isSubmitting}
                type="submit"
              >
                {isSubmitting ? t.submitting : t.submit}
              </button>
              <Link className="text-link" href={`/${locale}/login`}>
                {t.backToLogin}
              </Link>
            </div>
          </form>
        )}
      </section>
    </main>
  );
}
